import api from './api';
import type { User } from '@app-types/models/user';

interface GodModeStatusResponse {
  success: boolean;
  data: {
    hasGodMode: boolean;
  };
}

interface ImpersonatableUsersParams {
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
}

interface ImpersonatableUsersResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

interface ImpersonationResponse {
  success: boolean;
  data: {
    token: string;
    user: User;
  };
}

interface ExitImpersonationResponse {
  success: boolean;
  message?: string;
}

/**
 * Check if the current user has God Mode privileges
 */
export const checkGodModeStatus = async (): Promise<GodModeStatusResponse> => {
  try {
    const response = await api.get<GodModeStatusResponse>('/admin/godmode/status');
    return response.data;
  } catch (error: unknown) {
    // If 403, user doesn't have God Mode
    const axiosErr = error as { response?: { status?: number } };
    if (axiosErr.response?.status === 403) {
      return { success: false, data: { hasGodMode: false } };
    }
    throw error;
  }
};

/**
 * Fetch list of users available for impersonation
 */
export const getImpersonatableUsers = async ({ search = '', type = '', page = 1, limit = 20 }: ImpersonatableUsersParams = {}): Promise<ImpersonatableUsersResponse> => {
  const params: Record<string, string | number> = { page, limit };
  if (search) params.search = search;
  if (type) params.type = type;

  const response = await api.get<ImpersonatableUsersResponse>('/admin/godmode/users', { params });
  return response.data;
};

/**
 * Start impersonating a user
 */
export const startImpersonation = async (userId: string): Promise<ImpersonationResponse> => {
  const response = await api.post<ImpersonationResponse>(`/admin/godmode/impersonate/${userId}`);
  return response.data;
};

/**
 * Exit impersonation session (for audit trail)
 */
export const exitImpersonation = async (): Promise<ExitImpersonationResponse> => {
  const response = await api.post<ExitImpersonationResponse>('/admin/godmode/exit');
  return response.data;
};
